import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { cellarLabel, type CellarEntry } from '../auth/cellar-list';
import { useCellarList } from '../auth/CellarListContext';
import { t } from '../i18n';
import type { SettingsLinkState } from './CellarSelector';
import './TopBar.css';

/**
 * The bar on top of every signed-in screen (T072, FR-040, SC-014) and the tab bar at the bottom
 * (owner request, 2026-10-01).
 *
 * - The top bar always names the active cellar, and turns a different colour while the owner is
 *   inside someone else's cellar so nothing is logged in the wrong one.
 * - The tab bar: "Accueil" (the cellar's home page), "Stock" (the wines, A to Z), "Échéances"
 *   (wines past or near their window, owner 2026-10-03) and "Carte" (User Story 10). They open
 *   the active cellar; elsewhere (settings, users), the person's own.
 * - "Réglages" (where the owner also finds "Admin" and the cellar switch) is the gear in the top
 *   bar's right corner since 2026-10-03.
 */
export function TopBar({ active }: { active?: CellarEntry }) {
  const { person } = useAuth();
  const { cellars } = useCellarList();
  if (!person) return null;
  const shown: CellarEntry = active ??
    cellars?.find((entry) => entry.own) ?? { id: person.id, name: '', own: true };
  const base = `/c/${shown.id}`;

  return (
    <>
      <header className={shown.own ? 'top-bar' : 'top-bar top-bar--other-cellar'}>
        <Link to={base} className="top-bar__cellar-name">
          {cellarLabel(shown)}
        </Link>
        {/* "Réglages" in the top right corner (owner, 2026-10-03), freeing a tab below. */}
        <Link
          to="/reglages"
          state={{ fromCellar: shown.id } satisfies SettingsLinkState}
          className="top-bar__settings"
          aria-label={t('cellar.settings')}
        >
          <TabIcon name="settings" />
        </Link>
      </header>
      <nav className="tab-bar" aria-label={t('nav.label')}>
        <NavLink to={base} end className="tab-bar__item">
          <TabIcon name="home" />
          {t('nav.home')}
        </NavLink>
        <NavLink to={`${base}/stock`} className="tab-bar__item">
          <TabIcon name="stock" />
          {t('nav.stock')}
        </NavLink>
        <NavLink to={`${base}/echeances`} className="tab-bar__item">
          <TabIcon name="expiring" />
          {t('nav.expiring')}
        </NavLink>
        <NavLink to={`${base}/carte`} className="tab-bar__item">
          <TabIcon name="map" />
          {t('map.link')}
        </NavLink>
      </nav>
    </>
  );
}

/** Simple line icons, drawn here so nothing is downloaded. */
function TabIcon({ name }: { name: 'home' | 'stock' | 'expiring' | 'map' | 'settings' }) {
  return (
    <svg
      className="tab-bar__icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {name === 'home' && <path d="M3 11.5 12 4l9 7.5M5.5 9.5V20h5v-6h3v6h5V9.5" />}
      {name === 'stock' && (
        // A bottle.
        <path d="M10 3h4v4.5c2 1 3 2.6 3 4.5v8a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-8c0-1.9 1-3.5 3-4.5z" />
      )}
      {name === 'expiring' && (
        // An hourglass.
        <path d="M7 3h10M7 21h10M8 3c0 4 8 5 8 9s-8 5-8 9M16 3c0 4-8 5-8 9s8 5 8 9" />
      )}
      {name === 'map' && (
        <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.7 3.5 5.7 3.5 9s-1 6.3-3.5 9c-2.5-2.7-3.5-5.7-3.5-9s1-6.3 3.5-9z" />
      )}
      {name === 'settings' && (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 13.5l1.6 1.2-2 3.4-1.9-.7a7 7 0 0 1-2 1.2L14.8 21h-4l-.3-2.4a7 7 0 0 1-2-1.2l-1.9.7-2-3.4 1.6-1.2a7 7 0 0 1 0-2.3L4.6 10l2-3.4 1.9.7a7 7 0 0 1 2-1.2L10.8 3h4l.3 2.4a7 7 0 0 1 2 1.2l1.9-.7 2 3.4-1.6 1.2a7 7 0 0 1 0 2.3z" />
        </>
      )}
    </svg>
  );
}
